'use client';
import { Search, Sparkles, Calendar, PartyPopper } from 'lucide-react';
import { useTranslations } from 'next-intl';

export function HowItWorks() {
  const t = useTranslations('landing.howItWorks');

  const steps = [
    {
      number: '01',
      icon: Search,
      title: t('step1Title'),
      description: t('step1Desc'),
      color: 'from-primary/20 to-primary/5',
      iconBg: 'bg-primary/10',
      iconColor: 'text-primary',
    },
    {
      number: '02',
      icon: Sparkles,
      title: t('step2Title'),
      description: t('step2Desc'),
      color: 'from-violet-500/20 to-violet-500/5',
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
    },
    {
      number: '03',
      icon: Calendar,
      title: t('step3Title'),
      description: t('step3Desc'),
      color: 'from-emerald-500/20 to-emerald-500/5',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
    },
    {
      number: '04',
      icon: PartyPopper,
      title: t('step4Title'),
      description: t('step4Desc'),
      color: 'from-amber-500/20 to-amber-500/5',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
    },
  ] as const;

  return (
    <section
      id="how-it-works"
      className="section-padding relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-accent/30 to-background" />

      {/* Decorative elements */}
      <div className="absolute top-1/4 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-secondary/10 rounded-full blur-3xl" />

      <div className="container-landing relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent text-sm font-medium mb-6 border border-primary/20">
            {t('sectionBadge')}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display tracking-tight text-balance">
            {t('heading')}{' '}
            <span className="text-gradient">{t('headingHighlight')}</span>
          </h2>
          <p className="mt-4 md:mt-6 text-lg md:text-xl text-muted-foreground">
            {t('subheading')}
          </p>
        </div>

        {/* Steps - Desktop horizontal timeline */}
        <div className="hidden lg:block">
          <div className="relative">
            {/* Connection line */}
            <div className="absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-primary/50 via-violet-500/50 via-emerald-500/50 to-amber-500/50" />

            {/* Steps */}
            <div className="grid grid-cols-4 gap-8">
              {steps.map((step, index) => (
                <div key={step.number} className="relative">
                  {/* Connector dot */}
                  <div className="absolute top-24 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-background border-2 border-primary z-10" />

                  {/* Card */}
                  <div
                    className={`relative p-6 rounded-2xl bg-gradient-to-b ${step.color} border border-white/50 backdrop-blur-sm`}
                  >
                    {/* Number */}
                    <div className="absolute -top-4 left-6 px-3 py-1 bg-foreground text-background text-sm font-bold rounded-full">
                      {step.number}
                    </div>

                    {/* Icon */}
                    <div
                      className={`w-14 h-14 ${step.iconBg} rounded-xl flex items-center justify-center mt-4 mb-5`}
                    >
                      <step.icon className={`w-7 h-7 ${step.iconColor}`} />
                    </div>

                    {/* Content */}
                    <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  {/* Arrow (except last) */}
                  {index < steps.length - 1 && (
                    <div className="absolute top-24 -right-4 text-muted-foreground/30 rtl:rotate-180">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Steps - Mobile/Tablet vertical timeline */}
        <div className="lg:hidden">
          <div className="relative">
            {/* Connection line */}
            <div className="absolute top-0 bottom-0 left-6 w-0.5 bg-gradient-to-b from-primary via-violet-500 via-emerald-500 to-amber-500" />

            {/* Steps */}
            <div className="space-y-8">
              {steps.map((step) => (
                <div key={step.number} className="relative flex gap-6">
                  {/* Timeline dot */}
                  <div className="relative z-10 flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-background border-2 border-primary flex items-center justify-center">
                      <step.icon className={`w-5 h-5 ${step.iconColor}`} />
                    </div>
                  </div>

                  {/* Card */}
                  <div
                    className={`flex-1 p-5 rounded-xl bg-gradient-to-br ${step.color} border border-white/50`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-2 py-0.5 bg-foreground text-background text-xs font-bold rounded">
                        {step.number}
                      </span>
                      <h3 className="font-semibold">{step.title}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 md:mt-20 text-center">
          <p className="text-lg text-muted-foreground mb-6">
            {t('bottomText')}
          </p>
          <a
            href="#"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-shadow"
          >
            {t('bottomCta')}
            <svg
              className="w-4 h-4 rtl:rotate-180"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
